/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { ListItemCard } from '@/shared/ui';
import { Text } from '@/shared/ui';
import { cn } from '@/shared/ui/utils/tailwind';
import { IDLE_TONE_COLOR, idleToneFor } from '@features/deals/utils/deal-state';
import { LeaseStateBadge } from './LeaseStateBadge';
import { daysSinceLeaseTouched, isLeaseOpen } from '../constants/lease-triage';
import { LEASE_STATE_STYLES } from '../constants/lease-states';
import type { LeaseState } from '../constants/lease-states';
import type { Lease } from '../types/lease.types';

export interface LeaseBoardCardProps {
  agencyId: string;
  lease: Lease;
  /** Tenant name already resolved by the page's client lookup. */
  tenantName: string;
  /** Property address already resolved by the page's property lookup. */
  /** Null while the property's label is still loading: no line yet. */
  propertyLabel: string | null;
}

/**
 * One lease on the board: the tenant, the property, and the exact state.
 *
 * The badge stays even though the column already groups by state, because
 * two of the columns cover two states each — without it, a card in
 * "Encerradas" cannot say whether the contract was rescinded or wound up.
 * It is the same `LeaseStateBadge` the table column renders, so the two
 * views cannot drift into describing the same lease differently.
 *
 * The property is clamped to two lines: these addresses come from the
 * property registry and run long enough to bury the tenant's name above
 * them, the same problem the attendance card solves the same way.
 *
 * Under the badge sits how long the lease has stood still, in the same
 * shape and the same week the attendance card uses — a broker moving
 * between the two boards should not have to learn a second vocabulary
 * for "parado".
 *
 * What is *not* here is the rent: whether this month's competência is
 * late is the first thing a broker would want, and the list endpoint does
 * not carry it. Showing a stale or guessed figure would be worse than
 * showing none, so the card says what it can date and nothing more.
 */
export function LeaseBoardCard({
  agencyId, lease, tenantName, propertyLabel,
}: LeaseBoardCardProps) {
  const { t } = useTranslation('features/leases');
  const navigate = useNavigate();
  const accent = LEASE_STATE_STYLES[lease.state as LeaseState];
  const idleDays = daysSinceLeaseTouched(lease);
  // A finished contract still says when it was last touched, but never in
  // the alarm colour: it is not behind on anything. Without this the card
  // painted a rescinded lease cobre while the triage band — correctly —
  // left it out of the stalled count, so the two disagreed on screen.
  const idleTone = isLeaseOpen(lease) ? idleToneFor(idleDays) : idleToneFor(idleDays) && 'quiet';

  return (
    <div data-testid="lease-board-card" data-state={lease.state}>
      <ListItemCard
        accentColor={accent?.color}
        firstName={tenantName || t('board.card.unnamedTenant')}
        description={propertyLabel === null ? undefined : propertyLabel || t('board.card.noProperty')}
        descriptionClamp={2}
        onClick={() => navigate(`/app/agencies/${agencyId}/leases/${lease.id}`)}
      >
        <LeaseStateBadge state={lease.state} />

        {idleTone && (
          <Text
            component="span"
            data-testid="lease-card-idle"
            data-idle-tone={idleTone}
            style={{ color: IDLE_TONE_COLOR[idleTone] }}
            className={cn(
              'flex items-center gap-(--space-1-5)',
              'text-[length:var(--text-meta)] [font-weight:var(--font-semibold)]',
            )}
          >
            <div
              aria-hidden="true"
              className="h-1.5 w-1.5 shrink-0 rounded-full"
              style={{ background: IDLE_TONE_COLOR[idleTone] }}
            />
            {idleDays === 0
              ? t('board.card.touchedToday')
              : t('board.card.idleDays', { count: idleDays as number })}
          </Text>
        )}
      </ListItemCard>
    </div>
  );
}

export default LeaseBoardCard;
